
import globalConstants from '../../shared/globalConstants';
import { mapActions, mapGetters } from 'vuex';
import TrailerAssessmentType from '../../types/TrailerAssessmentType';
import { AssociationType } from '../../types/AssociationType';
import { ActionTypes } from '@/store/actionTypes';
import { MutationTypes } from '@/store/mutationTypes';
import TrailerAssessment from '../../types/TrailerAssessment';
import { defineComponent, PropType } from 'vue';
import ConfirmReportedDamage from './Confirmations/ConfirmReportedDamage.vue';
import AcceptWithDamageConfirmation from './Confirmations/AcceptWithDamageConfirmation.vue';
import GoBackConfirmation from './Confirmations/GoBackConfirmation.vue';
import RejectWithDamageConfirmation from './Confirmations/RejectWithDamageConfirmation.vue';
import ConfirmPreviousReportedDamage from './Confirmations/ConfirmPreviousReportedDamage.vue';
import ConfirmPreTripReportedDamage from './Confirmations/ConfirmPreTripReportedDamage.vue';
import DotSubmissionConfirmation from './Confirmations/DotSubmissionConfirmation.vue';
import PreviousDamageSafetyConfirmation from './Confirmations/PreviousDamageSafetyConfirmation.vue';
import SomethingWrongOverlay from '@/components/SomethingWrongOverlay.vue';
import FbxButton from '@/components/fbx-button.vue';
import SvgKingpinLandingGearVue from '@/components/Icons/SvgKingpinLandingGear.vue';
import SvgAirlinesElectricalVue from '@/components/Icons/SvgAirlinesElectrical.vue';
import SvgOtherTrailer from '@/components/Icons/SvgOtherTrailer.vue';
import SvgBrakes from '@/components/Icons/SvgBrakes.vue';
import SvgTires from '@/components/Icons/SvgTires.vue';
import SvgLights from '@/components/Icons/SvgLights.vue';
import SvgDamage from '@/components/Icons/SvgDamage.vue';
import { isNullOrUndefined } from '@/shared/helpers';
import FbaApiError from '@/types/FbaApiError';
import queries from '@/queries';
import Trailer from '@/types/Trailer';
import LastTrailerAssessmentResult from '@/types/LastTrailerAssessmentResult';
import { findKey, omit } from 'lodash';
import {
    TrailerAssessmentAnswer,
    TrailerAssessmentAnswers,
    TrailerAssessmentQuestion,
} from '@/types/TrailerAssessmentQuestion';
import { TrailerAssessmentOutcome } from '@/types/TrailerAssessmentOutcome';
import useAssociation from '@/shared/composables/useAssociation';
import ConfirmationEventPayload from '@/types/ConfirmationEventPayload';
import checkTrailerAttachability from '@/shared/mixins/checkTrailerAttachability';
import DriverLoaded from '@/components/DriverLoaded.vue';
import getTrailerAlreadyAttachedToTruck from '@/shared/mixins/GetTrailerAlreadyAttachedToTruck';
import DotInspectionOverdueConfirmation from './Confirmations/DotInspectionOverdueConfirmation.vue';
import { VIRTUAL_RED_TAG_ORIGIN } from '@/types/WorkflowTypes';
import { VirtualRedTagNotificationQuery } from '@/types/VirtualRedTagNotification';

export default defineComponent({
    components: {
        AcceptWithDamageConfirmation,
        ConfirmReportedDamage,
        GoBackConfirmation,
        RejectWithDamageConfirmation,
        ConfirmPreviousReportedDamage,
        ConfirmPreTripReportedDamage,
        PreviousDamageSafetyConfirmation,
        DotInspectionOverdueConfirmation,
        DotSubmissionConfirmation,
        SomethingWrongOverlay,
        FbxButton,
        SvgKingpinLandingGearVue,
        SvgAirlinesElectricalVue,
        SvgOtherTrailer,
        SvgBrakes,
        SvgTires,
        SvgLights,
        SvgDamage,
        DriverLoaded,
    },
    data(): {
        carrierName: string | undefined;
        driverMobileNumber: string | undefined;
        brakes: keyof TrailerAssessment;
        tires: keyof TrailerAssessment;
        lights: keyof TrailerAssessment;
        damage: keyof TrailerAssessment;
        kingpinLanding: keyof TrailerAssessment;
        airlinesElectrical: keyof TrailerAssessment;
        other: keyof TrailerAssessment;
        showBodyDamageConfirmation: boolean;
        showButtonLoadingTake: boolean;
        showButtonLoadingRedTag: boolean;
        isViewLoading: boolean;
        fromDetachModal: boolean;
        showDotConfirmation: boolean;
        showDotConfirmationForExpiredDot: boolean;
        showPreviousDamageConfirmation: boolean;
        previousDamagePresent: boolean;
        skipReportedDamageDotPrompt: boolean;
        isDotSafe: boolean;
        showLoadedTrailerModal: boolean;
        currentAttachedTrailerNumber: string | null;
        currentAttachedTrailerCompany: string | null;
        submittedAssessmentId: string | undefined;
    } {
        return {
            carrierName: undefined,
            driverMobileNumber: undefined,
            brakes: globalConstants.trailer.assessmentTypes
                .brakes as keyof TrailerAssessment,
            tires: globalConstants.trailer.assessmentTypes
                .tires as keyof TrailerAssessment,
            lights: globalConstants.trailer.assessmentTypes
                .lights as keyof TrailerAssessment,
            damage: globalConstants.trailer.assessmentTypes
                .damage as keyof TrailerAssessment,
            kingpinLanding: globalConstants.trailer.assessmentTypes
                .kingpinLanding as keyof TrailerAssessment,
            airlinesElectrical: globalConstants.trailer.assessmentTypes
                .airlinesElectrical as keyof TrailerAssessment,
            other: globalConstants.trailer.assessmentTypes
                .other as keyof TrailerAssessment,
            showBodyDamageConfirmation: false,
            showButtonLoadingTake: false,
            showButtonLoadingRedTag: false,
            isViewLoading: false,
            fromDetachModal: false,
            showDotConfirmation: false,
            showDotConfirmationForExpiredDot: false,
            showPreviousDamageConfirmation: false,
            previousDamagePresent: false,
            skipReportedDamageDotPrompt: false,
            isDotSafe: false,
            showLoadedTrailerModal: false,
            currentAttachedTrailerNumber: null,
            currentAttachedTrailerCompany: null,
            submittedAssessmentId: undefined,
        };
    },
    props: {
        trailerNumber: {
            type: String,
            required: true,
        },
        company: {
            type: String,
            required: true,
        },
        association: {
            type: String as PropType<AssociationType>,
            default: AssociationType.ATTACH,
        },
        origin: {
            type: String,
            default: 'findtrailer',
        },
    },
    setup() {
        const { result: associationResult, updateAssociation } =
            useAssociation();

        return {
            associationResult,
            updateAssociation,
        };
    },
    computed: {
        ...mapGetters({
            trailerAssessment: 'trailerAssessment',
            selectedTrailer: 'selectedTrailer',
            trailerSelectionMethod: 'getTrailerSelectionMethod',
            driver: 'getDriver',
            lastActiveTrailerAssessmentResult:
                'lastActiveTrailerAssessmentResult',
            trailerAssessmentMeta: 'trailerAssessmentMeta',
            bestAvailableLocation: 'getPinnedBestAvailableLocation',
            dotExpirationAccepted: 'dotExpirationAccepted',
        }),
        isVirtualRedTagFlow(): boolean {
            return this.origin === VIRTUAL_RED_TAG_ORIGIN;
        },
        assessmentEntryPoint(): string {
            if (this.isVirtualRedTagFlow) {
                return globalConstants.trailer.trailerAssessment.entryPoints
                    .virtualRedTag;
            }

            return globalConstants.trailer.trailerAssessment.entryPoints.attach;
        },
        error(): FbaApiError | undefined {
            return this.$store.getters.getError;
        },
        hasError() {
            return (
                !isNullOrUndefined(this?.error?.message) &&
                this.error?.reasonCode !==
                    globalConstants.reasonCodes.trailerNotDetachable
            );
        },
        hasDamage(): boolean {
            return !isNullOrUndefined(
                findKey(
                    this.trailerAssessment,
                    (type: TrailerAssessmentType) => type.typeHasDamage === true
                )
            );
        },
        hasCompleted(): boolean {
            if (!this.trailerAssessment) {
                return false;
            }

            return Object.values(this.trailerAssessment).every(
                (type: any) => !isNullOrUndefined(type?.typeHasDamage)
            );
        },
        possessionClass(): string {
            return this.hasCompleted
                ? 'btn btn-primary'
                : 'btn btn-primary disabled';
        },
        canConfirmVirtualRedTag(): boolean {
            // A VR assessment only needs one thumbs-down to justify red tagging, unlike
            // the normal attach flow it doesn't require every area to be assessed.
            return this.hasDamage;
        },
        virtualRedTagClass(): string {
            return this.canConfirmVirtualRedTag
                ? 'btn btn-primary'
                : 'btn btn-primary disabled';
        },
        withDamageClass(): string {
            return this.hasDamage
                ? 'btn btn-outline-primary italics'
                : 'btn btn-outline-primary italics disabled';
        },
        showDotExpiredModal() {
            return (
                this.isAttach === true &&
                !this.isVirtualRedTagFlow &&
                this.dotExpirationAccepted !== true &&
                this.isAssessmentTrailerDotExpired
            );
        },
        submitButtonText() {
            if (this.showButtonLoadingTake) {
                return 'Submitting';
            }

            return this.association === AssociationType.DETACH
                ? 'Save and Submit'
                : "I'm ready to take possession";
        },
        isAssessmentForPreviousTrailer() {
            return (
                !isNullOrUndefined(this.trailerNumber) &&
                !isNullOrUndefined(this.selectedTrailer) &&
                this.trailerNumber !== this.selectedTrailer.trailerNumber
            );
        },
        isAttach() {
            return this.association !== AssociationType.DETACH;
        },
        isAssessmentTrailerDotExpired() {
            return (
                this.isAssessmentForPreviousTrailer === false &&
                this.selectedTrailer?.isDotInspectionExpired === true
            );
        },
        // undefined outside the VR flow so the non-VR "Virtual Red Tag" button (which
        // shares this same modal instance) keeps the component's default copy.
        dotSafeConfirmationTitle(): string | undefined {
            return this.isVirtualRedTagFlow
                ? 'To the best of your knowledge is this trailer DOT compliant and safe to perform a load with?'
                : undefined;
        },
    },
    async created() {
        if (
            isNullOrUndefined(this.trailerAssessment) &&
            this.$route?.query?.confirmation
        ) {
            const query = {
                ...(this.$route.query.association && {
                    association: this.$route.query.association,
                }),
                ...(this.$route.query.origin && {
                    origin: this.$route.query.origin,
                }),
            };

            // until state management is created, clear query string (except
            // association/origin, which the rest of the flow depends on)
            // and remove popups if there is no trailer assessment
            this.$router.replace({
                name: 'TrailerAssessment',
                query,
            });

            return;
        }

        try {
            await this.initializeDataAndStore();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            console.error(e);
        }
    },
    async mounted() {
        await this.beginTrailerAssessment();

        this.fromDetachModal = this.$route.params?.fromDetachModal === 'true';

        // Initialize trailer photos store if not already initialized
        if (
            !this.$store.getters.trailerPhotosInitialized &&
            this.selectedTrailer
        ) {
            await this.$store.dispatch(ActionTypes.INITIALIZE_TRAILER_PHOTOS, {
                association: this.association,
                driverDetails: this.driver,
                trailerId: this.selectedTrailer.id,
            });
        }
    },
    mixins: [checkTrailerAttachability, getTrailerAlreadyAttachedToTruck],
    methods: {
        ...mapActions({
            beginTrailerAssessment: ActionTypes.BEGIN_TRAILER_ASSESSMENT,
        }),
        async fetchDriver() {
            await this.$store.dispatch(ActionTypes.FETCH_DRIVER, {
                company: this.driver.company,
                driverCode: this.driver.driverCode,
            });
        },
        async initializeDataAndStore(): Promise<void> {
            this.isViewLoading = true;
            if (this.lastActiveTrailerAssessmentResult === undefined) {
                await this.checkExistingTrailerDamage();
            }

            if (this.driver) {
                this.driverMobileNumber = this.driver.phoneNumbers[0].number;
            }

            await this.validateTrailer();
            this.isViewLoading = false;
        },
        goBackToBeginningOfAssessment(): void {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
            this.$router.push({
                name: 'TrailerAssessment',
                query: omit(this.$route.query, 'confirmation'),
            });
            this.$router.go(0);
        },
        async checkExistingTrailerDamage(): Promise<void> {
            await this.$store.dispatch(
                ActionTypes.FETCH_LAST_ACTIVE_TRAILER_ASSESSMENT,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                }
            );

            this.notePreviousDefectsForAssessment(TrailerAssessmentAnswer.NO);

            if (
                this.lastActiveTrailerAssessmentResult instanceof
                LastTrailerAssessmentResult
            ) {
                const { assessmentDriver } =
                    this.lastActiveTrailerAssessmentResult;
                if (this.isAttach) {
                    this.notePreviousDefectsForAssessment(
                        TrailerAssessmentAnswer.YES
                    );
                    this.$router.push({
                        query: {
                            confirmation: 'confirmexistingdamage',
                            ...this.$route.query,
                        },
                    });
                } else if (
                    this.lastActiveTrailerAssessmentResult?.eventName ===
                        AssociationType.ATTACH &&
                    assessmentDriver?.driverFirstName ===
                        this.driver?.firstName &&
                    assessmentDriver?.driverLastName ===
                        this.driver?.lastName &&
                    assessmentDriver?.driverMobileNumber ===
                        this.driver?.phoneNumbers[0]?.number
                ) {
                    this.notePreviousDefectsForAssessment(
                        TrailerAssessmentAnswer.YES
                    );
                    this.$router.push({
                        query: {
                            confirmation: 'confirmpretripgdamage',
                            ...this.$route.query,
                        },
                    });
                }
            }
        },
        closeModal(): void {
            this.$store.dispatch(ActionTypes.CLEAR_ERROR, undefined);
        },
        setTypeStatus(
            type: keyof TrailerAssessment,
            typeHasDamage: boolean,
            destination?: string
        ): void {
            const damage = new TrailerAssessmentType(type, typeHasDamage);

            this.$store.dispatch(ActionTypes.UPDATE_TRAILER_ASSESSMENT, damage);
            if (typeHasDamage) {
                this.$router.push({
                    name: destination,
                    query: this.$route.query,
                    params: {
                        trailerNumber: this.trailerNumber,
                        subType:
                            type === this.other
                                ? globalConstants.trailer.assessmentTypes.other
                                : undefined,
                    },
                });
            }
        },
        getActiveClass(type: keyof TrailerAssessment): string {
            if (isNullOrUndefined(this.trailerAssessment)) return '';
            else if (this.trailerAssessment[type].typeHasDamage === false)
                return 'active-good';
            else if (this.trailerAssessment[type].typeHasDamage === true)
                return 'active-bad';
            else return '';
        },
        async validateTrailer(): Promise<void> {
            // This screen serves both the VR and normal attach flows. VR eligibility
            // allows Refrigerated trailers, so a trailer that already passed VR
            // confirmation must not be re-rejected here as "invalid type".
            const trailer = await queries.getTrailerById(
                this.trailerNumber,
                this.company,
                this.isVirtualRedTagFlow
            );
            if (isNullOrUndefined(trailer) || !(trailer instanceof Trailer)) {
                if (
                    this.fromDetachModal === true &&
                    trailer.reasonCode ===
                        globalConstants.reasonCodes.invalidTrailerType
                ) {
                    await this.updateAssociation(
                        this.association,
                        this.trailerNumber,
                        this.company,
                        this.bestAvailableLocation
                    );
                    this.$router.push({
                        name: 'AssociateTrailerSuccess',
                        query: {
                            association: this.association,
                            trailerNumber: this.trailerNumber,
                            trailerCompany: this.company,
                            hasBodyDamage: 'false',
                            fromDetachModal: 'true',
                        },
                    });
                } else {
                    this.$store.commit(
                        MutationTypes.SET_ERROR,
                        trailer as FbaApiError
                    );
                }
            }
        },
        async onAcceptTrailer(): Promise<void> {
            this.showButtonLoadingTake = true;
            if (this.hasDamage) {
                this.$router.push({
                    query: {
                        confirmation: 'confirmdamage',
                        ...this.$route.query,
                    },
                });
            } else {
                const trailerNumber = this.trailerNumber;
                const trailerCompany = this.company;
                const hasBodyDamage =
                    this.showBodyDamageConfirmation.toString();

                const response = await this.confirmTrailer();

                if (
                    !isNullOrUndefined(response) &&
                    this.error?.reasonCode ===
                        globalConstants.reasonCodes.loadedTrailerNotDetachable
                ) {
                    const attachedTrailer =
                        await this.getTrailerAlreadyAttachedToTruck();

                    this.currentAttachedTrailerNumber =
                        attachedTrailer?.trailerNumber ?? null;
                    this.currentAttachedTrailerCompany =
                        attachedTrailer?.company ?? null;
                    this.showLoadedTrailerModal = true;
                    return;
                }

                if (
                    !isNullOrUndefined(response) &&
                    (response === true ||
                        this.error?.reasonCode ===
                            globalConstants.reasonCodes.trailerNotDetachable)
                ) {
                    this.$router.push({
                        name: 'AssociateTrailerSuccess',
                        query: {
                            association: this.association,
                            trailerNumber,
                            trailerCompany,
                            hasBodyDamage,
                            fromDetachModal: String(this.fromDetachModal),
                        },
                    });
                }
            }
            this.showButtonLoadingTake = false;
        },
        checkPreviousDamage(): void {
            if (this.isVirtualRedTagFlow === true) {
                return;
            }

            if (this.previousDamagePresent === true)
                this.showPreviousDamageConfirmationModal();
            else if (
                this.isAttach === true &&
                this.hasDamage !== true &&
                this.isAssessmentTrailerDotExpired
            ) {
                this.showDotSafeConfirmationForExpiredDot();
            } else this.onAcceptTrailer();
        },
        async showDotSafeConfirmation(): Promise<void> {
            // A VR trailer with an expired DOT is already known to be non-compliant, so
            // asking is moot: skip straight to red tagging instead of prompting. The
            // modal's own reject handler would otherwise be what records this answer,
            // so it has to be recorded here too.
            if (
                this.isVirtualRedTagFlow &&
                this.isAssessmentTrailerDotExpired
            ) {
                this.$store.dispatch(
                    ActionTypes.UPDATE_TRAILER_ASSESSMENT_QUESTION,
                    {
                        question: TrailerAssessmentQuestion.DOT_SAFE,
                        answer: TrailerAssessmentAnswer.NO,
                    }
                );
                await this.onRedTag();
                return;
            }

            this.showDotConfirmation = true;
        },
        showDotSafeConfirmationForExpiredDot(): void {
            this.showDotConfirmationForExpiredDot = true;
        },
        showPreviousDamageConfirmationModal(): void {
            this.showPreviousDamageConfirmation = true;
        },
        hidePreviousDamageConfirmation(
            confirmation: ConfirmationEventPayload
        ): void {
            this.skipReportedDamageDotPrompt = true;
            this.showPreviousDamageConfirmation = false;
            this.isDotSafe = confirmation.didAccept;
        },
        confirmPreviousDamagePresent(result: ConfirmationEventPayload): void {
            this.previousDamagePresent = result.didAccept;
        },
        async onRedTag(): Promise<void> {
            this.showButtonLoadingRedTag = true;
            const trailerNumber = this.trailerNumber;
            const trailerCompany = this.company;

            this.noteOutcomeForAssessment(
                TrailerAssessmentOutcome.VIRTUAL_RED_TAG
            );

            const response = await this.postAssessment();
            if (response !== false && !isNullOrUndefined(response)) {
                if (this.isVirtualRedTagFlow) {
                    this.$router.push({
                        name: 'VirtualRedTagRedTagged',
                        params: { trailerNumber },
                        // Lets the next screens offer to save a notification
                        // preference against this red tag.
                        query: {
                            [VirtualRedTagNotificationQuery.assessmentId]:
                                this.submittedAssessmentId,
                        },
                    });
                } else {
                    this.$router.push({
                        name: 'AttachTrailerRedTag',
                        query: {
                            trailerNumber,
                            trailerCompany,
                            hasBodyDamage: 'true',
                        },
                    });
                }
            }
            this.showButtonLoadingRedTag = false;
        },
        async postAssessment(): Promise<boolean> {
            this.submittedAssessmentId = undefined;
            const hasPhotos = this.$store.getters.trailerPhotosCount > 0;
            const assessmentResponse = await this.$store.dispatch(
                ActionTypes.POST_TRAILER_ASSESSMENT,
                {
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    eventName: this.association,
                    hasPhotos,
                    source: globalConstants.trailer.trailerAssessment.source,
                    entryPoint: this.assessmentEntryPoint,
                }
            );

            if (assessmentResponse instanceof FbaApiError) {
                this.$store.commit(
                    MutationTypes.SET_ERROR,
                    assessmentResponse as FbaApiError
                );
                return false;
            }

            if (
                assessmentResponse?.didSucceed &&
                assessmentResponse?.assessmentId
            ) {
                this.submittedAssessmentId = String(
                    assessmentResponse.assessmentId
                );
            }

            if (
                assessmentResponse?.didSucceed &&
                assessmentResponse?.assessmentId &&
                hasPhotos
            ) {
                await this.$store.dispatch(
                    ActionTypes.SUBMIT_TRAILER_PHOTOS,
                    assessmentResponse.assessmentId
                );
            }

            return assessmentResponse?.didSucceed;
        },
        async confirmTrailer(): Promise<boolean> {
            const trailerQr = this.selectedTrailer?.trailerQr;
            const selectionMethod = this.trailerSelectionMethod;
            this.noteOutcomeForAssessment(
                TrailerAssessmentOutcome.TOOK_POSSESSION
            );

            const assessmentRes = await this.postAssessment();
            if (!assessmentRes) return false;

            if (this.association == AssociationType.ATTACH) {
                const available = await this.checkTrailerAttachability({
                    trailerNumber: this.trailerNumber,
                    trailerCompany: this.company,
                    driverId: this.driver.id,
                });

                if (this.trailerAndDriverAreLoaded === true) {
                    const attachedTrailer =
                        await this.getTrailerAlreadyAttachedToTruck();

                    this.currentAttachedTrailerNumber =
                        attachedTrailer?.trailerNumber ?? null;
                    this.currentAttachedTrailerCompany =
                        attachedTrailer?.company ?? null;
                    this.showLoadedTrailerModal = true;
                    return false;
                }

                if (available == false) {
                    this.$router.push({
                        name: 'TrailerUnavailable',
                        query: {
                            trailerNumber: this.trailerNumber,
                            trailerCompany: this.company,
                        },
                    });
                    return false;
                }
            }

            this.$store.commit(MutationTypes.CLEAR_SELECTED_TRAILER, undefined);

            await this.updateAssociation(
                this.association,
                this.trailerNumber,
                this.company,
                this.bestAvailableLocation,
                trailerQr,
                selectionMethod
            );

            return assessmentRes && (this.associationResult ?? false);
        },
        onNavigateBack(): void {
            this.$store.commit(
                MutationTypes.SET_LAST_ACTIVE_TRAILER_ASSESSMENT_RESULT,
                undefined
            );
            this.$router.push({
                query: {
                    association: this.association,
                    confirmation: 'gobackconfirmation',
                },
            });
        },
        cancelVirtualRedTag(): void {
            this.$store.dispatch(
                ActionTypes.CLEAR_TRAILER_ASSESSMENT,
                undefined
            );
            this.$router.push({ name: 'VirtualRedTagEntry' });
        },

        notePreviousDefectsForAssessment(
            answer: TrailerAssessmentAnswers
        ): void {
            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_QUESTION,
                {
                    question:
                        TrailerAssessmentQuestion.PREVIOUS_DEFECTS_REPORTED,
                    answer: answer,
                }
            );
        },
        noteOutcomeForAssessment(outcome: TrailerAssessmentOutcome): void {
            this.$store.dispatch(
                ActionTypes.UPDATE_TRAILER_ASSESSMENT_OUTCOME,
                outcome
            );
        },
        acceptDotExpired() {
            this.$store.commit(MutationTypes.SET_DOT_EXPIRATION_ACCEPTED, true);
        },
    },
});
